import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ThemeSelector from "../../../theme/ThemeSelector";
import { UserPrefsApi } from "../../../api/common/apiIndex";
import Loader from "../commonUtils/Loader";
import { useTheme } from "../../../theme/ThemeProvider";

const DEFAULT_NOTIF_PREFS = {
  emailEnabled: false,
  transactionAlerts: false,
  balanceAlerts: false,
};

const mapFromServer = (dto) => ({
  theme: dto?.theme ? String(dto.theme).toLowerCase() : "system",
  emailEnabled: Boolean(dto?.emailEnabled),
  transactionAlerts: Boolean(dto?.transactionAlerts),
  balanceAlerts: Boolean(dto?.balanceAlerts),
  locale: dto?.locale ?? null,
  timezone: dto?.timezone ?? null,
});

const buildPayload = ({ theme, notif, meta }) => ({
  theme: (theme || "system").toUpperCase(),
  emailEnabled: Boolean(notif.emailEnabled),
  transactionAlerts: Boolean(notif.transactionAlerts),
  balanceAlerts: Boolean(notif.balanceAlerts),
  locale: meta.locale ?? null,
  timezone: meta.timezone ?? null,
});

const shallowEqual = (a, b) => {
  const keys = Object.keys({ ...a, ...b });
  return keys.every((key) => a?.[key] === b?.[key]);
};

export default function Preferences() {
  const queryClient = useQueryClient();
  const { theme, setTheme, refreshPreferences } = useTheme();

  const [notifPrefs, setNotifPrefs] = useState(() => ({ ...DEFAULT_NOTIF_PREFS }));
  const [baseNotifPrefs, setBaseNotifPrefs] = useState(() => ({ ...DEFAULT_NOTIF_PREFS }));
  const [meta, setMeta] = useState({ locale: null, timezone: null });
  const [initialTheme, setInitialTheme] = useState(theme ?? "system");

  const {
    data: fetchedPrefs,
    isLoading: prefsLoading,
    isError: prefsError,
    error,
  } = useQuery({
    queryKey: ["me", "preferences"],
    queryFn: async () => {
      try {
        return await UserPrefsApi.getMyPrefs();
      } catch (err) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
    select: (res) => mapFromServer(res),
    retry: 1,
  });

  useEffect(() => {
    if (prefsLoading) return;

    if (prefsError) {
      const message =
        error?.response?.data?.message || error?.message || "Failed to load preferences";
      toast.error(message);
      setNotifPrefs({ ...DEFAULT_NOTIF_PREFS });
      setBaseNotifPrefs({ ...DEFAULT_NOTIF_PREFS });
      setMeta({ locale: null, timezone: null });
      setInitialTheme("system");
      if (typeof setTheme === "function") setTheme("system");
      return;
    }

    if (fetchedPrefs == null) {
      setNotifPrefs({ ...DEFAULT_NOTIF_PREFS });
      setBaseNotifPrefs({ ...DEFAULT_NOTIF_PREFS });
      setMeta({ locale: null, timezone: null });
      setInitialTheme("system");
      if (typeof setTheme === "function") setTheme("system");
      return;
    }

    const { theme: incomingTheme, locale, timezone, ...rest } = fetchedPrefs;
    const normalized = {
      emailEnabled: rest.emailEnabled,
      transactionAlerts: rest.transactionAlerts,
      balanceAlerts: rest.balanceAlerts,
    };
    setNotifPrefs(normalized);
    setBaseNotifPrefs({ ...normalized });
    setMeta({ locale, timezone });
    setInitialTheme(incomingTheme);
    if (incomingTheme && typeof setTheme === "function") setTheme(incomingTheme);
  }, [prefsLoading, prefsError, error, fetchedPrefs, setTheme]);

  const notifDirty =
    !prefsLoading && !prefsError && !shallowEqual(notifPrefs, baseNotifPrefs);
  const themeDirty = !prefsLoading && !prefsError && initialTheme !== (theme ?? "system");
  const isDirty = notifDirty || themeDirty;

  const { mutate: savePrefs, isPending: saving } = useMutation({
    mutationFn: (payload) => UserPrefsApi.updateMyPrefs(payload),
    onSuccess: () => {
      setBaseNotifPrefs({ ...notifPrefs });
      setInitialTheme(theme ?? "system");
      queryClient.invalidateQueries({ queryKey: ["me", "preferences"] });
      if (typeof refreshPreferences === "function") {
        void refreshPreferences();
      }
      toast.success("Preferences updated");
    },
    onError: (err) => {
      const message = err?.response?.data?.message || err?.message || "Failed to update preferences";
      toast.error(message);
    },
  });

  if (prefsLoading) {
    return (
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4 d-flex justify-content-center">
          <Loader text="Loading preferences…" />
        </div>
      </div>
    );
  }

  if (prefsError) {
    return (
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <div className="alert alert-danger mb-0" role="alert">
            Unable to load preferences. Please try again later.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-3 p-md-4">
          <h6 className="mb-3">Appearance</h6>

          <div className="mb-3">
            <div className="form-text text-uppercase small mb-1">Theme</div>
            <ThemeSelector size="md" />
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-3 p-md-4 position-relative">
          <Loader
            variant="overlay"
            size="sm"
            text={prefsLoading ? "Loading…" : "Saving…"}
            show={prefsLoading || saving}
            backdrop={false}
          />
          <h6 className="mb-3">Notifications</h6>

          <div className="row g-3">
            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="pref-email"
                  checked={notifPrefs.emailEnabled}
                  onChange={(e) => setNotifPrefs((prev) => ({ ...prev, emailEnabled: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="pref-email">
                  Email notifications
                </label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="pref-tx"
                  checked={notifPrefs.transactionAlerts}
                  onChange={(e) => setNotifPrefs((prev) => ({ ...prev, transactionAlerts: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="pref-tx">
                  Transaction alerts
                </label>
              </div>
              <div className="form-check mt-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="pref-balance"
                  checked={notifPrefs.balanceAlerts}
                  onChange={(e) => setNotifPrefs((prev) => ({ ...prev, balanceAlerts: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="pref-balance">
                  Balance alerts
                </label>
              </div>
            </div>
          </div>

          <div className="mt-3 d-flex gap-2">
            <button
              className="btn btn-primary rounded-pill"
              onClick={() => savePrefs(buildPayload({ theme, notif: notifPrefs, meta }))}
              disabled={!isDirty || saving || prefsLoading}
              aria-busy={saving || prefsLoading}
            >
              Save preferences
            </button>
            <button
              className="btn btn-outline-secondary rounded-pill"
              onClick={() => {
                setNotifPrefs({ ...baseNotifPrefs });
                if (typeof setTheme === "function") setTheme(initialTheme);
              }}
              disabled={!isDirty || saving || prefsLoading}
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
